import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "../../assets/scss/Settings/Settings.module.scss";
import dummyProfile from "../../assets/images/Profile.png";
import { BaseURL } from "../../lib/api";
import { getUserInfo } from "../../utils/user.util";
import { useGetUserByIdQuery } from "../../slices/admin/admin";

const ThermalPrintSettings = () => {
    const localUser = getUserInfo();
    const userId = localUser?._id;
    const navigate = useNavigate();

    const { data: apiData, refetch } = useGetUserByIdQuery(userId, { skip: !userId });

    const [selectedImage, setSelectedImage] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [showLogo, setShowLogo] = useState(localUser?.isPrintLogo ?? true);
    const [printerOption, setPrinterOption] = useState(localUser?.printerOption || "");
    const [showTerms, setShowTerms] = useState("");
    const [termsText, setTermsText] = useState("");

    useEffect(() => {
        if (apiData?.result?.length > 0) {
            const user = apiData.result[0];
            setShowLogo(user.isPrintLogo ?? true);
            setPrinterOption(user.printerOption || "");
            setShowTerms(user.isTnc ? "yes" : "no");
            setTermsText(user.tnc || "");

            if (user.store?.logo) {
                const logoUrl = user.store.logo.startsWith("http")
                    ? user.store.logo
                    : `${BaseURL}/${user.store.logo}`;
                setSelectedImage(logoUrl);
            }
        }
    }, [apiData]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error("Logo must be below 2MB");
                return;
            }
            setSelectedImage(URL.createObjectURL(file));
            setLogoFile(file);
        }
    };

    const handleSave = async () => {
        try {
            const formData = new FormData();
            formData.append("isPrintLogo", showLogo);
            formData.append("printerOption", printerOption);
            formData.append("isTnc", showTerms === "yes");
            formData.append("tnc", termsText);
            if (logoFile) formData.append("printerLogo", logoFile);

            const rawToken = localStorage.getItem("token");
            const token = rawToken?.replace(/^"|"$/g, "");

            const response = await axios.put(`${BaseURL}/user/update/settings`, formData, {
                headers: {
                    authorization: token,
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success(response.data.message || "Thermal Print Settings updated successfully!");
            refetch();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update settings");
        }
    };

    return (
        <div className={styles.settingsContainer}>
            <div className={styles.headerRow}>
                <h2 className={styles.header}>Thermal Print Settings</h2>
            </div>

            <div className={styles.card}>
                <div className={styles.dflex}>
                    <div>
                        <div className={styles.widthcontent}>
                            <label className={styles.label}>Select Printer</label>
                            <select className={styles.inputs} value={printerOption} onChange={(e) => setPrinterOption(e.target.value)}>
                                <option value="">Select Printer</option>
                                <option value="80">Thermal Printer 80mm</option>
                                <option value="58">Thermal Printer 58mm</option>
                            </select>
                        </div>

                        <div className={styles.widthcontent}>
                            <label className={styles.label}>Enable Terms and Conditions</label>
                            <select className={styles.inputs} value={showTerms} onChange={(e) => setShowTerms(e.target.value)}>
                                <option value="">Select Terms and Conditions</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>

                        {showTerms === "yes" && (
                            <div className={styles.textareaContainer}>
                                <label className={styles.label}>Terms and Conditions</label>
                                <textarea
                                    className={styles.textarea}
                                    value={termsText}
                                    onChange={(e) => setTermsText(e.target.value)}
                                    placeholder="Type your terms here..."
                                    rows={4}
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles.widthcontent}>
                        <div className={styles.toggleLogoBox}>
                            <span className={styles.toggleText}>{showLogo ? "Logo Shown" : "Logo Hidden"}</span>
                            <div className={`${styles.toggleSwitch} ${showLogo ? styles.on : styles.off}`} onClick={() => setShowLogo(!showLogo)}>
                                <div className={styles.toggleCircle}></div>
                            </div>
                        </div>

                        {showLogo && (
                            <div className={styles.imageContainer}>
                                <img src={selectedImage || dummyProfile} alt="Company Logo" className={styles.profileImage} />
                                <div style={{ marginTop: "20px" }}>
                                    <label htmlFor="file-upload" className={styles.changeImageButton}>Upload Org Logo</label>
                                    <input id="file-upload" type="file" accept="image/png, image/jpeg" onChange={handleImageChange} style={{ display: "none" }} />
                                    <p className={styles.imageInfo}>Upload image below 2MB (JPG, PNG)</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.footerButtons}>
                    <button className={styles.cancel} onClick={() => navigate('/dashboard')}>Cancel</button>
                    <button className={styles.save} onClick={handleSave}>Save</button>
                </div>
            </div>
        </div>
    );
};

export default ThermalPrintSettings;
